// NotesTreeList.js - Refactored for Workspace/Document Architecture
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FolderOpen, FileText, Code, Image, Star, Clock, ChevronRight, Plus, MoreHorizontal } from 'lucide-react';
import styles from './NotesTreeList.module.css';
import { useCategories } from '../contexts/CategoriesContext';
import { useWorkspaces } from '../contexts/WorkspacesContext';  // ✅ FIXED: removed extra quote
import { formatDistanceToNow } from 'date-fns';
import { getCurrentUser } from '../utils/authService';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ConfirmModal from '../ConfirmModal';

const WorkspaceIcon = React.memo(({ workspace, hasDocuments, isExpanded, toggleExpand }) => {
  // If this is a workspace with documents, make the entire icon clickable for expansion
  if (hasDocuments) {
    return (
      <div className={styles.folderIconContainer}>
        <button 
          className={styles.folderButton}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpand(e);
          }}
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          <FolderOpen className={styles.folderIcon} />
        </button>
      </div>
    );
  }

  // For empty workspaces, show folder icon
  return <FolderOpen className={styles.folderIcon} />;
});


const NotesTreeList = ({ 
  onDocumentSelect,              // ✅ RENAMED: was onNoteSelect
  visibleCategoryIds = null, 
  expandedCategories, 
  setExpandedCategories, 
  sortOrder = 'newest', 
  folderSort = 'none' 
}) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [contextMenu, setContextMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    type: null,
    itemId: null
  });

  const { categories, removeCategory, removeCategoryFromState, refreshCategories, reorderCategories } = useCategories();
  
  // ✅ UPDATED: Get workspaces and documents from context
const { 
  workspaces, 
  documents,
  selectedDocumentPublicId,
  selectedWorkspacePublicId,  // ✅ ADD THIS
  loadDocuments,
  loadDocumentContent,
  deleteWorkspace,
  selectDocument,
  selectWorkspace,            // ✅ ADD THIS
  currentView,
  loadAllWorkspaces,
  loadRecentWorkspaces
} = useWorkspaces();

  // Memoize expanded workspaces state initialization
  const [expandedWorkspaces, setExpandedWorkspaces] = useState(() => {
    try {
      const saved = localStorage.getItem('expandedWorkspaces');  // ✅ RENAMED: was expandedNotes
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  const [deleteCategoryModal, setDeleteCategoryModal] = useState({
    isOpen: false,
    categoryId: null,
    categoryName: '',
    workspacesCount: 0,  // ✅ RENAMED: was notesCount
    imagesCount: 0,
    isDeleting: false
  });

  const [deleteWorkspaceModal, setDeleteWorkspaceModal] = useState({  // ✅ RENAMED: was deleteNoteModal
    isOpen: false,
    workspaceId: null,   // ✅ RENAMED: was noteId
    workspaceName: '',   // ✅ RENAMED: was noteName
    isDeleting: false
  });

  const [deleteDocumentModal, setDeleteDocumentModal] = useState({  // ✅ NEW: for document deletion
    isOpen: false,
    documentId: null,
    documentName: '',
    isDeleting: false
  });

  // ✅ UPDATED: Sort workspaces function (was sortNotes)
  const sortWorkspaces = useCallback((workspaces, sortOrder, folderSort, documents) => {
    return workspaces.sort((a, b) => {
      // Get documents for each workspace
      const aDocuments = documents.filter(d => d.workspace_public_id === a.public_id);
      const bDocuments = documents.filter(d => d.workspace_public_id === b.public_id);
      const aHasDocuments = aDocuments.length > 0;
      const bHasDocuments = bDocuments.length > 0;
      
      if (folderSort === 'top') {
        if (aHasDocuments && !bHasDocuments) return -1;
        if (!aHasDocuments && bHasDocuments) return 1;
      } else if (folderSort === 'bottom') {
        if (aHasDocuments && !bHasDocuments) return 1;
        if (!aHasDocuments && bHasDocuments) return -1;
      }
      
      if (sortOrder === 'newest') {
        return new Date(b.modified_date) - new Date(a.modified_date);
      } else if (sortOrder === 'oldest') {
        return new Date(a.modified_date) - new Date(b.modified_date);
      } else if (sortOrder === 'title') {
        return (a.name || 'Untitled').localeCompare(b.name || 'Untitled');
      }
      return 0;
    });
  }, []);

  // ✅ UPDATED: Optimized workspacesByCategory (was notesByCategory)
  const workspacesByCategory = useMemo(() => {
    if (!categories || !workspaces) return [];

    const topLevelWorkspaces = workspaces.filter(ws => ws); // All workspaces are top-level now
    
    // Create workspaces map by category
    const workspacesMap = new Map();
    topLevelWorkspaces.forEach(workspace => {
      const categoryId = workspace.primary_category_id || 1;
      if (!workspacesMap.has(categoryId)) {
        workspacesMap.set(categoryId, []);
      }
      workspacesMap.get(categoryId).push(workspace);
    });

    let filteredCategories = categories;
    if (visibleCategoryIds && visibleCategoryIds.length > 0) {
      filteredCategories = categories.filter(cat => visibleCategoryIds.includes(cat.id));
    }

    return filteredCategories.map(category => {
      // Handle Recent virtual category
      if (category.is_virtual && category.id === 'recent') {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        const recentWorkspaces = topLevelWorkspaces
          .filter(ws => new Date(ws.modified_date) > oneWeekAgo)
          .sort((a, b) => new Date(b.modified_date) - new Date(a.modified_date))
          .slice(0, 5);

        return {
          ...category,
          workspaces: recentWorkspaces  // ✅ RENAMED: was notes
        };
      }
      
      // Handle regular categories
      const categoryWorkspaces = workspacesMap.get(category.id) || [];
      return {
        ...category,
        workspaces: sortWorkspaces([...categoryWorkspaces], sortOrder, folderSort, documents)
      };
    });
  }, [categories, workspaces, documents, visibleCategoryIds, sortOrder, folderSort, sortWorkspaces]);

  // Memoized callbacks to prevent unnecessary re-renders
  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category.id);
  }, []);

  const toggleExpandWorkspace = useCallback((workspacePublicId) => {
    setExpandedWorkspaces(prev => {
      const newExpandedWorkspaces = new Set(prev);
      if (prev.has(workspacePublicId)) {
        newExpandedWorkspaces.delete(workspacePublicId);
      } else {
        newExpandedWorkspaces.add(workspacePublicId);
        // Load documents for this workspace when expanding
        if (loadDocuments) {
          loadDocuments(workspacePublicId);
        }
      }
      // Batch localStorage update to avoid frequent writes
      requestAnimationFrame(() => {
        localStorage.setItem('expandedWorkspaces', JSON.stringify([...newExpandedWorkspaces]));
      });
      return newExpandedWorkspaces;
    });
  }, [loadDocuments]);

  // ✅ UPDATED: Delete handlers
  const handleConfirmDeleteWorkspace = async () => {
    setDeleteWorkspaceModal(prev => ({ ...prev, isDeleting: true }));
    
    try {
      await deleteWorkspace(deleteWorkspaceModal.workspaceId);
      setDeleteWorkspaceModal({ isOpen: false, workspaceId: null, workspaceName: '', isDeleting: false });
    } catch (error) {
      console.error('Error deleting workspace:', error);
      alert('Failed to delete workspace. Please try again.');
      setDeleteWorkspaceModal(prev => ({ ...prev, isDeleting: false }));
    }
  };

  const handleConfirmDeleteDocument = async () => {
    setDeleteDocumentModal(prev => ({ ...prev, isDeleting: true }));
    
    try {
      await deleteDocument(deleteDocumentModal.documentId);
      setDeleteDocumentModal({ isOpen: false, documentId: null, documentName: '', isDeleting: false });
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
      setDeleteDocumentModal(prev => ({ ...prev, isDeleting: false }));
    }
  };

  const handleConfirmDeleteCategory = async () => {
    setDeleteCategoryModal(prev => ({ ...prev, isDeleting: true }));
    
    try {
      const user = getCurrentUser();
      const forceResponse = await fetch(
        `/api/users/${user.user_id}/categories/${deleteCategoryModal.categoryId}?force=true`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        }
      );
      
      if (!forceResponse.ok) {
        throw new Error('Failed to delete category');
      }
      
      await removeCategoryFromState(deleteCategoryModal.categoryId);
      setDeleteCategoryModal({ 
        isOpen: false, 
        categoryId: null, 
        categoryName: '', 
        workspacesCount: 0,
        imagesCount: 0, 
        isDeleting: false 
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category. Please try again.');
      setDeleteCategoryModal(prev => ({ ...prev, isDeleting: false }));
    }
  };

const handleCategoryDragEnd = useCallback(async (result) => {
  if (!result.destination) return;
  
  const sourceIndex = result.source.index;
  const destIndex = result.destination.index;
  
  if (sourceIndex === destIndex) return;
  
  // Step 1: Manually reorder the array
  const reorderedCategories = Array.from(workspacesByCategory);
  const [movedCategory] = reorderedCategories.splice(sourceIndex, 1);
  reorderedCategories.splice(destIndex, 0, movedCategory);
  
  // Step 2: Update UI immediately (optimistic update)
  reorderCategories(reorderedCategories);
  
  // Step 3: Save to backend in background
  const reorderData = reorderedCategories
    .filter(cat => !cat.is_virtual)
    .map((cat, index) => ({
      category_id: cat.id,
      new_order: index
    }));
  
  try {
    const user = getCurrentUser();
    const response = await fetch(`/api/users/${user.user_id}/categories/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        user_id: user.user_id,
        reorder_data: reorderData
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to save category order');
    }
  } catch (error) {
    console.error('❌ Error saving category order:', error);
    // Revert on error by reloading from server
    await refreshCategories();
    alert('Failed to save category order');
  }
}, [workspacesByCategory, reorderCategories, refreshCategories]);

const handleContextMenu = useCallback((e, type, itemId) => {
  e.preventDefault();
  e.stopPropagation();
  
  // Get the tree container to account for its scroll position
  const treeContainer = e.currentTarget.closest('.notebooksTree');
  const scrollOffset = treeContainer ? treeContainer.scrollTop : 0;
  
  setContextMenu({
    show: true,
    x: e.clientX - 100,
    y: e.clientY + scrollOffset,  // ← ADD scroll offset
    type: type,
    itemId: itemId
  });
}, []);

  const handleContextMenuClick = async (action, type, itemId) => {
    setContextMenu({ show: false, x: 0, y: 0, type: null, itemId: null });
    
    if (action === 'add_document') {  // ✅ RENAMED: was add_subnote
      try {
        const newDocument = await createDocument(itemId);
        if (newDocument) {
          setExpandedWorkspaces(prev => new Set([...prev, itemId]));
          setTimeout(() => {
            onDocumentSelect(newDocument.public_id);
          }, 100);
        }
      } catch (error) {
        console.error('Error creating document:', error);
      }
    } else if (action === 'rename' && type === 'workspace') {
      const workspace = workspaces.find(w => w.public_id === itemId);
      if (workspace) {
        const newName = prompt('Enter new name:', workspace.name);
        if (newName && newName !== workspace.name) {
          try {
            await updateDocument(itemId, { name: newName });
          } catch (error) {
            console.error('Error renaming workspace:', error);
          }
        }
      }
    } else if (action === 'rename' && type === 'document') {
      const document = documents.find(d => d.public_id === itemId);
      if (document) {
        const newTitle = prompt('Enter new title:', document.title);
        if (newTitle && newTitle !== document.title) {
          try {
            await updateDocument(itemId, { title: newTitle });
          } catch (error) {
            console.error('Error renaming document:', error);
          }
        }
      }
    } else if (action === 'delete' && type === 'workspace') {
      const workspace = workspaces.find(w => w.public_id === itemId);
      if (workspace) {
        setDeleteWorkspaceModal({
          isOpen: true,
          workspaceId: itemId,
          workspaceName: workspace.name,
          isDeleting: false
        });
      }
    } else if (action === 'delete' && type === 'document') {
      const document = documents.find(d => d.public_id === itemId);
      if (document) {
        setDeleteDocumentModal({
          isOpen: true,
          documentId: itemId,
          documentName: document.title,
          isDeleting: false
        });
      }
    } else if (action === 'rename' && type === 'category') {
      const category = categories.find(c => c.id === itemId);
      if (category) {
        const newName = prompt('Enter new category name:', category.name);
        if (newName && newName !== category.name) {
          try {
            const user = getCurrentUser();
            const response = await fetch(
              `/api/users/${user.user_id}/categories/${itemId}`,
              {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name: newName })
              }
            );
            if (response.ok) {
              await refreshCategories();
            }
          } catch (error) {
            console.error('Error renaming category:', error);
          }
        }
      }
    } else if (action === 'delete' && type === 'category') {
      const category = categories.find(c => c.id === itemId);
      if (category) {
        const categoryWorkspaces = workspacesByCategory.find(c => c.id === itemId)?.workspaces || [];
        setDeleteCategoryModal({
          isOpen: true,
          categoryId: itemId,
          categoryName: category.name,
          workspacesCount: categoryWorkspaces.length,
          imagesCount: 0,
          isDeleting: false
        });
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.show) {
        setContextMenu({ show: false, x: 0, y: 0, type: null, itemId: null });
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu.show]);

  // ✅ UPDATED: Render workspace item (was renderNoteItem)
  const renderWorkspaceItem = useCallback((workspace, isDocument = false) => {
    if (isDocument) {
      // This is a document within a workspace
      const isSelected = selectedDocumentPublicId === workspace.public_id;
      
      return (
        <div
          key={workspace.public_id}
          className={`${styles.treeNoteItem} ${styles.subnote} ${isSelected ? styles.selected : ''}`}
         
          onClick={() => {
              // Check if document is in a different workspace
              if (workspace.workspace_public_id !== selectedWorkspacePublicId) {
                // Switch to that workspace (which will auto-select first document)
                selectWorkspace(workspace.workspace_public_id);
              } else {
                // Same workspace - just select the document
                onDocumentSelect(workspace.public_id);
              }
            }}
          onContextMenu={(e) => handleContextMenu(e, 'document', workspace.public_id)}
        >
          <div className={styles.noteContent}>
            <div className={styles.noteIcon}>
              <FileText size={14} />
            </div>
            <span 
                className={styles.noteTitle}
                title={workspace.title || 'Untitled Document'}  /* ✅ ADD TOOLTIP */
              >
                {workspace.title || 'Untitled Document'}
              </span>
          </div>
          
          <div className={styles.noteActions}>
            <button 
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                handleContextMenu(e, 'document', workspace.public_id);
              }}
              title="More options"
            >
              <MoreHorizontal size={14} />
            </button>
          </div>
        </div>
      );
    }
    
    // Use document_count from backend instead of filtering local state
    const hasDocuments = (workspace.document_count || 0) > 0;
    const workspaceDocuments = documents.filter(d => d.workspace_public_id === workspace.public_id);
    const isExpanded = expandedWorkspaces.has(workspace.public_id);
    const isSelected = workspace.public_id === selectedWorkspacePublicId;
    
    // REPLACE the workspace return section with this:
return (
  <div 
    key={workspace.public_id} 
    className={`${styles.treeNoteItemWrapper} ${styles.workspacePanel} ${isSelected ? styles.active : ''}`}
  >
    {/* Workspace Header - NEW */}
    <div
        className={`${styles.workspaceHeader} ${isExpanded && hasDocuments ? styles.workspaceHeaderExpanded : ''}`}
        onClick={async () => {
          await selectWorkspace(workspace.public_id);
          if (hasDocuments && !isExpanded) {
            toggleExpandWorkspace(workspace.public_id);
          }
        }}
        onContextMenu={(e) => handleContextMenu(e, 'workspace', workspace.public_id)}
      >
        {/* Icon - moved left with less gap */}
        <span 
          className={styles.workspaceIconLeft} 
          style={{ 
            fontSize: '16px',
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.stopPropagation();  // ✅ Don't trigger workspace click
            toggleExpandWorkspace(workspace.public_id );
          }}
          title={isExpanded ? 'Collapse documents' : 'Expand documents'}
        >
          🟦
        </span>
        
        {/* Title with truncation and tooltip */}
        <span 
          className={styles.workspaceTitleText}
          title={workspace.name || 'Untitled Workspace'}  /* ✅ TOOLTIP */
        >
          {workspace.name || 'Untitled Workspace'}
        </span>
        
        {/* REMOVED DOC COUNT */}
        
        <button 
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleContextMenu(e, 'workspace', workspace.public_id);
          }}
          title="More options"
        >
          <MoreHorizontal size={14} />
        </button>
      </div>
    
    {/* Documents List */}
    {hasDocuments && isExpanded && (
      <div className={styles.workspaceDocuments}>
        {workspaceDocuments.map(document => renderWorkspaceItem(document, true))}
      </div>
    )}
  </div>
);
  }, [documents, expandedWorkspaces, selectedDocumentPublicId, onDocumentSelect, handleContextMenu, toggleExpandWorkspace, handleContextMenuClick]);

  return (
    <DragDropContext onDragEnd={handleCategoryDragEnd}>
      <Droppable droppableId="categories">
        {(provided) => (
          <div 
            className={styles.notebooksTree}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {workspacesByCategory.map((category, index) => (
              <Draggable 
                key={category.id} 
                draggableId={`category-${category.id}`}
                index={index}
                isDragDisabled={category.is_virtual}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={styles.categoryBlock}
                    style={{
                      ...provided.draggableProps.style,
                      opacity: snapshot.isDragging ? 0.5 : 1
                    }}
                  >
                    <div
                      className={`${styles.categoryHeader} ${expandedCategories.has(category.id) ? styles.expanded : ''}`}
                      onClick={(e) => {
                        handleCategorySelect(category); 
                        const newExpandedCategories = new Set(expandedCategories);
                        if (expandedCategories.has(category.id)) {
                          newExpandedCategories.delete(category.id);
                        } else {
                          newExpandedCategories.add(category.id);
                        }
                        setExpandedCategories(newExpandedCategories);
                        requestAnimationFrame(() => {
                          localStorage.setItem('expandedCategories', JSON.stringify([...newExpandedCategories]));
                        });
                      }}
                      onContextMenu={(e) => handleContextMenu(e, 'category', category.id)}
                    >
                      <div className={styles.categoryToggle}>
                        <ChevronRight 
                          className={expandedCategories.has(category.id) ? styles.expanded : ''} 
                          size={16}
                        />
                      </div>
                      
                      <div className={styles.categoryInfo}>
                        <span className={styles.categoryName}>
                          {category.name}
                        </span>
                        <span className={styles.noteCount}>
                          {category.workspaces.length}
                        </span>
                      </div>
                    </div>
                    
                    {expandedCategories.has(category.id) && (
                      <div className={styles.categoryContent}>
                        {category.workspaces.length > 0 ? (
                          category.workspaces.map(workspace => renderWorkspaceItem(workspace))
                        ) : (
                          <div className={styles.emptyCategory}>No workspaces in this category</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}

            {contextMenu.show && (
              <div 
                className={styles.contextMenu}
                style={{
                  position: 'fixed',
                  top: contextMenu.y,
                  left: contextMenu.x,
                  zIndex: 9999
                }}
              >
                {contextMenu.type === 'workspace' && (
                  <>
                    <div 
                      className={styles.contextMenuItem}
                      onClick={() => handleContextMenuClick('add_document', contextMenu.type, contextMenu.itemId)}
                    >
                      <Plus size={14} className={styles.menuItemIcon} />
                      Add Document
                    </div>

                    <div 
                      className={styles.contextMenuItem}
                      onClick={() => handleContextMenuClick('rename', contextMenu.type, contextMenu.itemId)}
                    >
                      <span className={styles.menuItemIcon}>✏️</span>
                      Rename
                    </div>
                    <div 
                      className={`${styles.contextMenuItem} ${styles.dangerItem}`}
                      onClick={() => handleContextMenuClick('delete', contextMenu.type, contextMenu.itemId)}
                    >
                      <span className={styles.menuItemIcon}>🗑️</span>
                      Delete
                    </div>
                  </>
                )}
                
                {contextMenu.type === 'document' && (
                  <>
                    <div 
                      className={styles.contextMenuItem}
                      onClick={() => handleContextMenuClick('rename', contextMenu.type, contextMenu.itemId)}
                    >
                      <span className={styles.menuItemIcon}>✏️</span>
                      Rename
                    </div>
                    <div 
                      className={`${styles.contextMenuItem} ${styles.dangerItem}`}
                      onClick={() => handleContextMenuClick('delete', contextMenu.type, contextMenu.itemId)}
                    >
                      <span className={styles.menuItemIcon}>🗑️</span>
                      Delete
                    </div>
                  </>
                )}
                
                {contextMenu.type === 'category' && (
                  <>
                    <div 
                      className={styles.contextMenuItem}
                      onClick={() => handleContextMenuClick('rename', contextMenu.type, contextMenu.itemId)}
                    >
                      <span className={styles.menuItemIcon}>✏️</span>
                      Rename
                    </div>
                    <div 
                      className={`${styles.contextMenuItem} ${styles.dangerItem}`}
                      onClick={() => handleContextMenuClick('delete', contextMenu.type, contextMenu.itemId)}
                    >
                      <span className={styles.menuItemIcon}>🗑️</span>
                      Delete
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Category Delete Modal */}
            <ConfirmModal
              isOpen={deleteCategoryModal.isOpen}
              onClose={() => setDeleteCategoryModal({ 
                isOpen: false, 
                categoryId: null, 
                categoryName: '', 
                workspacesCount: 0,
                imagesCount: 0, 
                isDeleting: false 
              })}
              onConfirm={handleConfirmDeleteCategory}
              title={`Delete category "${deleteCategoryModal.categoryName}"?`}
              message={`This will:
- Delete the category permanently
- Move ${deleteCategoryModal.workspacesCount} workspace(s) to "Inbox"
- Move ${deleteCategoryModal.imagesCount} image(s) to "Inbox"

Workspaces and images will be preserved in your Inbox category.`}
              confirmText="Delete Category"
              cancelText="Cancel"
              type="danger"
              isProcessing={deleteCategoryModal.isDeleting}
            />

            {/* Workspace Delete Modal */}
            <ConfirmModal
              isOpen={deleteWorkspaceModal.isOpen}
              onClose={() => setDeleteWorkspaceModal({ 
                isOpen: false, 
                workspaceId: null, 
                workspaceName: '', 
                isDeleting: false 
              })}
              onConfirm={handleConfirmDeleteWorkspace}
              title={`Delete workspace "${deleteWorkspaceModal.workspaceName}"?`}
              message="This will permanently delete this workspace and all its documents."
              confirmText="Delete Workspace"
              cancelText="Cancel"
              type="danger"
              isProcessing={deleteWorkspaceModal.isDeleting}
            />

            {/* Document Delete Modal */}
            <ConfirmModal
              isOpen={deleteDocumentModal.isOpen}
              onClose={() => setDeleteDocumentModal({ 
                isOpen: false, 
                documentId: null, 
                documentName: '', 
                isDeleting: false 
              })}
              onConfirm={handleConfirmDeleteDocument}
              title={`Delete document "${deleteDocumentModal.documentName}"?`}
              message="This will permanently delete this document."
              confirmText="Delete Document"
              cancelText="Cancel"
              type="danger"
              isProcessing={deleteDocumentModal.isDeleting}
            />
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default React.memo(NotesTreeList);